/**
 * TenantReferralWidget — appears on the tenant Dashboard.
 *
 * Shows the tenant's unique referral link, current $50-bounty wallet balance,
 * a copy-link button, and quick social-share rows. Tenants earn $50 CAD every
 * time a landlord they referred signs up at Central Rentals.
 */
import { useEffect, useState } from "react";
import { Copy, CheckCircle2, Gift, ExternalLink, Sparkles, Share2, Wallet, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/useRealtime";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function TenantReferralWidget() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cashoutBusy, setCashoutBusy] = useState(false);
  const [cashoutStatus, setCashoutStatus] = useState(null);

  // Live-refresh on wallet credit (sent by tenant_referral_service.signup_claim).
  useRealtime({
    topics: ["tenant-referral"],
    onEvent: (e) => {
      if (e.topic !== "tenant-referral") return;
      const credited = e.payload?.tenant_email?.toLowerCase() === (user?.email || "").toLowerCase();
      if (!credited) return;
      // Re-fetch the wallet snapshot — the SSE payload only carries the delta.
      fetch(`${API}/api/tenant-referral/wallet?email=${encodeURIComponent(user.email)}`)
        .then(r => r.ok ? r.json() : null)
        .then(w => { if (w) setWallet(w); toast.success(`+$${(e.payload.amount_cents/100).toFixed(0)} added to your wallet`); })
        .catch(() => {});
    },
  });

  useEffect(() => {
    if (!user?.email || user?.role !== "tenant") return;
    let mounted = true;
    (async () => {
      setBusy(true);
      try {
        // Idempotent — POST /start either returns an existing code or creates one.
        const r = await fetch(`${API}/api/tenant-referral/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user.email, name: user.name || null }),
        });
        if (!r.ok) throw new Error(await r.text());
        const d = await r.json();
        if (mounted) setData(d);
        // Fetch wallet for live balance
        const w = await fetch(`${API}/api/tenant-referral/wallet?email=${encodeURIComponent(user.email)}`);
        if (w.ok) {
          const wd = await w.json();
          if (mounted) setWallet(wd);
        }
        // Cash-out status
        const cs = await fetch(`${API}/api/tenant-referral/cashout/status?email=${encodeURIComponent(user.email)}`);
        if (cs.ok) {
          const csd = await cs.json();
          if (mounted) setCashoutStatus(csd);
        }
      } catch (e) {
        console.warn("Referral widget load failed", e);
      } finally {
        if (mounted) setBusy(false);
      }
    })();
    return () => { mounted = false; };
  }, [user?.email, user?.role, user?.name]);

  if (user?.role !== "tenant") return null;

  const shareUrl = data?.share_url || "";
  const balance = wallet?.balance_cad ?? 0;
  const signups = data?.signups || 0;

  const copy = () => {
    if (!shareUrl) return;
    // Fallback path if navigator.clipboard is unavailable (older Safari,
    // insecure context) — throws "The string did not match the expected
    // pattern." otherwise. document.execCommand keeps the button usable
    // everywhere and the try/catch ensures the widget never crashes.
    const safeCopy = async () => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(String(shareUrl));
        } else {
          const ta = document.createElement("textarea");
          ta.value = String(shareUrl);
          ta.style.position = "fixed"; ta.style.top = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopied(true);
        toast.success("Link copied — share it with your landlord!");
        setTimeout(() => setCopied(false), 2500);
      } catch (err) {
        console.warn("copy failed", err);
        toast.error("Copy didn't work — long-press the link and copy manually.");
      }
    };
    safeCopy();
  };

  const shareTo = (network) => {
    // Bail early — every network below expects a real shareUrl. This
    // guards against the widget rendering before the /start response
    // lands, which used to throw "The string did not match the expected
    // pattern." when window.open received an incomplete link.
    if (!shareUrl) {
      toast.error("Share link isn't ready yet — try again in a second.");
      return;
    }
    try {
      const safeUrl = String(shareUrl);
      const text = encodeURIComponent(
        `I'm using Central Rentals Canada for rent. Set up direct rent collection here — they're way better than email-money-transfer chaos: ${safeUrl}`
      );
      const url = encodeURIComponent(safeUrl);
      const links = {
        email: `mailto:?subject=${encodeURIComponent("Central Rentals Canada — for our rent")}&body=${text}`,
        sms: `sms:?&body=${text}`,
        whatsapp: `https://wa.me/?text=${text}`,
        twitter: `https://twitter.com/intent/tweet?text=${text}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      };
      const target = links[network];
      if (!target) {
        toast.error("That share option isn't available.");
        return;
      }
      window.open(target, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.warn("share failed", err);
      toast.error("Couldn't open share — try Copy link instead.");
    }
  };

  // ── Cash-out flow.
  // Step 1: open Stripe Express onboarding (if not yet completed).
  // Step 2: once payouts_enabled, transfer the wallet balance in one click.
  const startOnboarding = async () => {
    setCashoutBusy(true);
    try {
      const r = await fetch(`${API}/api/tenant-referral/cashout/onboard`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user?.email || "" }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      // Guard against Stripe returning a malformed URL — window.open on
      // an invalid URL throws "The string did not match the expected
      // pattern." on some browsers and freezes the button. Validate the
      // scheme first, then fail gracefully.
      const target = d?.onboarding_url || "";
      if (!/^https?:\/\//i.test(target)) {
        throw new Error("Stripe didn't return a valid onboarding link.");
      }
      window.open(target, "_blank", "noopener,noreferrer");
      toast.info("Finish bank verification in the new tab, then come back and click Cash out.");
    } catch (e) {
      toast.error(String(e?.message || e || "Onboarding failed"));
    } finally { setCashoutBusy(false); }
  };

  const cashout = async () => {
    setCashoutBusy(true);
    try {
      const r = await fetch(`${API}/api/tenant-referral/cashout/payout`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: user.email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Cash-out failed");
      toast.success(`Paid out $${d.amount_cad.toFixed(2)} CAD — lands in your bank next business day`);
      // Refresh wallet
      const w = await fetch(`${API}/api/tenant-referral/wallet?email=${encodeURIComponent(user.email)}`);
      if (w.ok) setWallet(await w.json());
    } catch (e) {
      toast.error(String(e.message || e));
    } finally { setCashoutBusy(false); }
  };

  const payoutsReady = cashoutStatus?.payouts_enabled;
  const minCad = (cashoutStatus?.min_cents || 5000) / 100;
  const canCashout = payoutsReady && balance >= minCad;

  return (
    <section
      data-testid="tenant-referral-widget"
      className="mb-6 rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white shadow-xl overflow-hidden relative"
    >
      <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none"/>
      <div className="absolute -bottom-12 -left-12 w-56 h-56 rounded-full bg-amber-200/15 blur-3xl pointer-events-none"/>
      <div className="relative px-5 sm:px-7 py-6 sm:py-7">
        <div className="flex items-center gap-2 text-emerald-100">
          <Gift className="w-4 h-4"/>
          <span className="text-[10px] uppercase tracking-[0.22em] font-bold">Earn $50 — refer your landlord</span>
        </div>
        <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-extrabold mt-2 leading-tight tracking-tight">
          Tell your landlord. Get <span className="text-amber-200">$50 CAD</span>.
        </h3>
        <p className="text-sm text-emerald-50/90 mt-1.5 max-w-xl">
          Every Canadian landlord that signs up using your link earns you $50, credited instantly to your wallet.
        </p>

        <div className="mt-5 grid grid-cols-3 gap-2 sm:gap-3 max-w-md">
          <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-100/80">Wallet</div>
            <div className="font-display text-xl font-extrabold mt-0.5 tabular-nums" data-testid="referral-balance">${balance.toFixed(0)}</div>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-100/80">Signups</div>
            <div className="font-display text-xl font-extrabold mt-0.5 tabular-nums" data-testid="referral-signups">{signups}</div>
          </div>
          <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide font-bold text-emerald-100/80">Per signup</div>
            <div className="font-display text-xl font-extrabold mt-0.5 tabular-nums">$50</div>
          </div>
        </div>

        <div className="mt-5 rounded-xl bg-slate-950/40 border border-white/15 px-4 py-3 flex items-center gap-3">
          <div className="text-xs font-mono text-emerald-100 truncate flex-1" data-testid="referral-link">{shareUrl || "Loading link…"}</div>
          <button
            onClick={copy}
            disabled={!shareUrl || busy}
            data-testid="referral-copy"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-emerald-700 font-bold text-xs hover:bg-emerald-50 transition-colors disabled:opacity-60"
          >
            {copied ? <><CheckCircle2 className="w-3.5 h-3.5"/> Copied</> : <><Copy className="w-3.5 h-3.5"/> Copy</>}
          </button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide font-bold text-emerald-100/80 mr-1">Share via</span>
          {["email","sms","whatsapp","twitter","facebook"].map((n) => (
            <button
              key={n}
              onClick={() => shareTo(n)}
              data-testid={`referral-share-${n}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 border border-white/15 text-[11px] font-bold capitalize transition-colors"
            >
              <Share2 className="w-3 h-3"/> {n}
            </button>
          ))}
        </div>

        {/* Cash-out controls */}
        <div className="mt-5 pt-4 border-t border-white/15 flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="text-[11px] text-emerald-100/80 flex-1">
            {payoutsReady
              ? <>Stripe connected. Cash out anytime your balance is ≥ ${minCad}.</>
              : cashoutStatus?.onboarded
                ? <>Stripe verification pending — finish in the new tab to unlock cash-out.</>
                : <>Connect Stripe (60 s) to cash out your wallet to your Canadian bank.</>}
          </div>
          {payoutsReady ? (
            <button
              onClick={cashout}
              disabled={!canCashout || cashoutBusy}
              data-testid="referral-cashout"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-300 text-amber-950 font-bold text-xs hover:bg-amber-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cashoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Wallet className="w-3.5 h-3.5"/>}
              {canCashout ? `Cash out $${balance.toFixed(2)}` : `Need $${minCad} minimum`}
            </button>
          ) : balance <= 0 ? (
            <span
              data-testid="referral-cashout-locked"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white/80 font-semibold text-xs"
            >
              <Wallet className="w-3.5 h-3.5"/> Refer a landlord to unlock
            </span>
          ) : (
            <button
              onClick={startOnboarding}
              disabled={cashoutBusy}
              data-testid="referral-onboard"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white text-emerald-700 font-bold text-xs hover:bg-emerald-50 transition-colors disabled:opacity-60"
            >
              {cashoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Wallet className="w-3.5 h-3.5"/>} Stripe cash out
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
